import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsBox,
  HsBoxHelper,
  HsClippingPlane,
  HsClippingStorage,
  HsColor,
  HsControlActionType,
  HsDefaultMaterial,
  HsGraphicObject,
  HsGraphicPoints,
  HsMatrix4,
  HsPointsMaterial,
  HsQuaternion,
  HsRegularGeometryData,
  HsShaderData,
  HsShaderObjectType,
  HsVector3,
  hsGenerateCubeGeometryData,
  hsGeneratePlaneGeometryData,
  hsIndexGeometry,
  hsMakeGeometry,
  hsMakeIndexedGeometry,
  type HsGeometry,
  type HsGraphicLines,
  type HsGraphics,
  type HsHypersceneTypes,
  type HsHypersceneViewer,
  type HsHypersceneViewerOptions,
  type HsMaterial,
  type HsRegularGeometry,
  type HsViewerInputOptions,
  type HsViewerPick,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makeLitMaterial } from '../../functions/make-lit-material.function';
import {
  closestAxisParameter,
  makeArrowGeometry,
  makeDirectionRotation,
  makeFaceMatrix,
  makePointerRay,
  type PointerRay,
} from './functions/clipping-tool-math.function';

/** Far from the world origin, to show that clipping keeps its precision there. */
const ORIGIN: HsHypersceneTypes.Array3 = [10000, 1000, -5000];
const CAMERA_POSITION: HsHypersceneTypes.Array3 = [2, 10, 30];
const GROUND_SIZE: number = 40;
const PLANE_CENTER: HsHypersceneTypes.Array3 = [0, 3, 5];
const PLANE_HALF_SIZE: number = 10;
const BOX_HALF_SIZE: number = 10;
const BOX_MIN_EXTENT: number = 2;
const BOX_MAX_DISTANCE: number = 100;
/** Clipping offset that discards nothing; shaders keep a fixed number of planes, so unused ones get it. */
const DISABLED_OFFSET: number = 1e9;
const TOOL_COLOR: HsHypersceneTypes.Array3 = [0.7, 0.7, 0.7];
/** Arrow colors by axis, as in the legacy tools: x red, y blue, z green. */
const ARROW_COLORS: HsHypersceneTypes.Array3[] = [
  [1, 0, 0],
  [0, 0, 1],
  [0, 0.7, 0],
];
const HIGHLIGHT_COLOR: HsHypersceneTypes.Array3 = [1, 0.85, 0.3];

enum ClippingMode {
  None,
  Plane,
  Cuboid,
}

enum HandleKind {
  /** Moves the single clipping plane. */
  Plane,
  /** Moves one face of the clipping box. */
  Face,
  /** Moves the whole clipping box. */
  Box,
}

interface ClippingHandle {
  readonly object: HsGraphicObject;
  readonly kind: HandleKind;
  readonly axis: number;
  /** 0 for the face at the box minimum, 1 for the one at the maximum. */
  readonly side: number;
  /** An arrow, or else a rectangle on the clipping plane. */
  readonly arrow: boolean;
}

function addOrigin(position: HsHypersceneTypes.Array3): HsHypersceneTypes.Array3 {
  return [position[0] + ORIGIN[0], position[1] + ORIGIN[1], position[2] + ORIGIN[2]];
}

function makeMatrix(
  position: HsHypersceneTypes.Array3,
  rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
  scale: HsHypersceneTypes.Array3 = [1, 1, 1]
): HsMatrix4 {
  return HsMatrix4.compose(position, rotation.values, scale);
}

function makeToolMaterial(gl: WebGL2RenderingContext, color: HsHypersceneTypes.Array3, alpha: number): HsMaterial {
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
  shaderData.addColor().addDepth().addNormals().addDiffuseLighting().addTransparency(alpha);
  const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
  material.color.set(...color);
  return material;
}

/**
 * Clipping planes: "Plane" cuts the scene with one movable plane, "Cuboid" keeps only what is inside a box whose
 * faces and position follow the arrows. The left mouse button drags the tools; the middle and right buttons,
 * the wheel and the keyboard move the camera.
 */
@Component({
  selector: 'hyperscene-clipping-planes',
  templateUrl: './clipping-planes.component.html',
  styleUrls: ['./clipping-planes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class ClippingPlanesDemo extends DemoViewerPage {
  public readonly planeToolTypesMap: Map<number, string> = new Map([
    [ClippingMode.None, 'None'],
    [ClippingMode.Plane, 'Plane'],
    [ClippingMode.Cuboid, 'Cuboid'],
  ]);

  public selectedPlaneTools: boolean[] = [true, false, false];

  /** Planes of every clipped material; their values change, their number does not. */
  private readonly clippingStorage: HsClippingStorage = new HsClippingStorage(
    Array.from({ length: 6 }, () => new HsClippingPlane(1, 0, 0, DISABLED_OFFSET))
  );
  private readonly boxMin: HsVector3 = new HsVector3(...addOrigin([-BOX_HALF_SIZE, -BOX_HALF_SIZE, -BOX_HALF_SIZE]));
  private readonly boxMax: HsVector3 = new HsVector3(...addOrigin([BOX_HALF_SIZE, BOX_HALF_SIZE, BOX_HALF_SIZE]));
  private readonly handles: Map<HsGraphicObject, ClippingHandle> = new Map();
  private readonly highlightMaterials: Map<HsGraphicObject, HsMaterial> = new Map();
  private readonly planeTools: HsGraphicObject[] = [];
  private readonly boxTools: HsGraphicObject[] = [];
  private readonly startBoxMin: HsVector3 = HsVector3.makeZeroVector();
  private readonly startBoxMax: HsVector3 = HsVector3.makeZeroVector();
  private readonly startAnchor: HsVector3 = HsVector3.makeZeroVector();
  private readonly listeners: (() => void)[] = [];
  private mode: ClippingMode = ClippingMode.None;
  private planeX: number = ORIGIN[0] + PLANE_CENTER[0];
  private startPlaneX: number = 0;
  private startParameter: number = 0;
  private boxLines: HsGraphicLines | null = null;
  private pointer: HsHypersceneTypes.Array2 | null = null;
  private pointerMoved: boolean = false;
  private dragged: ClippingHandle | null = null;
  private hovered: HsGraphicObject | null = null;
  private hoveredMaterial: HsMaterial | null = null;

  protected override viewerOptions(): Partial<HsHypersceneViewerOptions> {
    return { clearColor: [0.85, 0.92, 1, 1] };
  }

  protected override inputOptions(): HsViewerInputOptions {
    // The left button drives the tools; the middle and right buttons pan and orbit.
    return { dragActions: [null, HsControlActionType.MiddleMove, HsControlActionType.RightMove] };
  }

  protected initScene(viewer: HsHypersceneViewer): void {
    this.addSceneObjects(viewer);
    this.addPlaneTools(viewer);
    this.addBoxTools(viewer);
    this.updateTools(viewer);

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition(addOrigin(CAMERA_POSITION));
    viewer.camera.updateProjectionMatrix();
    viewer.onFrame(() => this.updateHover(viewer));
    const canvas: HTMLCanvasElement = viewer.surface.canvas;
    this.listen(canvas, 'mousedown', (event: MouseEvent) => this.startDrag(viewer, event));
    // Drags keep going when the pointer leaves the canvas.
    this.listen(canvas.ownerDocument, 'mousemove', (event: MouseEvent) => {
      this.pointer = [event.clientX, event.clientY];
      this.pointerMoved = true;
    });
    this.listen(canvas.ownerDocument, 'mouseup', () => {
      this.dragged = null;
      this.pointerMoved = true;
    });
  }

  /** Applies a drag before culling and keeps the box move arrows on the corner nearest the camera. */
  protected override animate(viewer: HsHypersceneViewer): boolean {
    if (this.dragged !== null && this.pointerMoved && this.pointer !== null) {
      this.pointerMoved = false;
      this.drag(this.dragged, makePointerRay(viewer, this.pointer[0], this.pointer[1]));
      this.updateTools(viewer);
      return true;
    }
    if (this.mode === ClippingMode.Cuboid && this.dragged === null) {
      // The nearest corner changes only when the camera moves, and a camera move already reruns culling.
      this.placeBoxMoveArrows(viewer);
    }
    return false;
  }

  public selectPlaneTool(planeToolType: number): void {
    this.selectedPlaneTools.fill(false);
    this.selectedPlaneTools[planeToolType] = true;
    this.mode = planeToolType;
    this.dragged = null;
    if (this.viewer !== null) {
      this.updateTools(this.viewer);
    }
  }

  public override ngOnDestroy(): void {
    this.listeners.forEach((remove: () => void) => remove());
    super.ngOnDestroy();
  }

  private makeClippedMaterial(gl: WebGL2RenderingContext, color: HsHypersceneTypes.Array3): HsMaterial {
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.setClippingStorage(this.clippingStorage);
    shaderData.addColor().addDepth().addNormals().addDiffuseLighting().addClipping();
    const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
    material.color.set(...color);
    return material;
  }

  private addObject(
    viewer: HsHypersceneViewer,
    name: string,
    geometry: HsGeometry,
    material: HsMaterial
  ): HsGraphicObject {
    const object: HsGraphicObject = new HsGraphicObject(getUuid(), name, geometry, material);
    viewer.add(object);
    return object;
  }

  private addSceneObjects(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const cubeData: HsRegularGeometryData = hsGenerateCubeGeometryData();
    const cubeGeometry: HsGeometry | null = hsMakeGeometry(graphics, cubeData);
    const indexedCubeGeometry: HsGeometry | null = hsMakeIndexedGeometry(graphics, hsIndexGeometry(cubeData));
    if (cubeGeometry === null || indexedCubeGeometry === null) {
      return;
    }
    const highlight: HsMaterial = this.makeClippedMaterial(viewer.gl, HIGHLIGHT_COLOR);
    const cube: HsGraphicObject = this.addObject(
      viewer,
      'cube',
      cubeGeometry,
      this.makeClippedMaterial(viewer.gl, [1, 1, 1])
    );
    cube.worldMatrix.copy(makeMatrix(addOrigin([5, 2, 5]), undefined, [2, 2, 2]));
    const indexedCube: HsGraphicObject = this.addObject(
      viewer,
      'indexed cube',
      indexedCubeGeometry,
      this.makeClippedMaterial(viewer.gl, [1, 0.75, 0])
    );
    indexedCube.worldMatrix.copy(makeMatrix(addOrigin([0, 2, 5]), undefined, [2, 2, 2]));
    [cube, indexedCube].forEach((object: HsGraphicObject) => {
      object.updateBoundingSphere();
      this.highlightMaterials.set(object, highlight);
    });

    // Instanced collections place their vertices relative to the camera, which the clipping planes do not
    // follow, so the ground is made of single cubes.
    const light: HsMaterial = this.makeClippedMaterial(viewer.gl, [1, 1, 1]);
    const dark: HsMaterial = this.makeClippedMaterial(viewer.gl, [0.3, 0.3, 0.3]);
    for (let i: number = 0; i < GROUND_SIZE; i++) {
      for (let j: number = 0; j < GROUND_SIZE; j++) {
        const material: HsMaterial = (i + j) % 2 === 1 ? dark : light;
        const tile: HsGraphicObject = this.addObject(viewer, 'ground', indexedCubeGeometry, material);
        const position: HsHypersceneTypes.Array3 = addOrigin([i * 2 - GROUND_SIZE, -2, j * 2 - GROUND_SIZE]);
        tile.worldMatrix.copy(makeMatrix(position, undefined, [0.999, 0.999, 0.999]));
        tile.updateBoundingSphere();
      }
    }
    this.addPointCloud(viewer);
  }

  private addPointCloud(viewer: HsHypersceneViewer): void {
    const side: number = 20;
    const vertices: number[] = [];
    const colors: number[] = [];
    for (let i: number = 0; i < side; i++) {
      for (let j: number = 0; j < side; j++) {
        for (let k: number = 0; k < side; k++) {
          const x: number = (i / side) * 2 - 1;
          const y: number = (j / side) * 2 - 1;
          const z: number = (k / side) * 2 - 1;
          if (x * x + y * y + z * z < 1) {
            vertices.push(x, y, z);
            colors.push(i / side, j / side, k / side);
          }
        }
      }
    }
    const geometry: HsRegularGeometry | null = hsMakeGeometry(
      viewer.surface.graphics,
      new HsRegularGeometryData(vertices, colors)
    );
    if (geometry === null) {
      return;
    }
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.setClippingStorage(this.clippingStorage);
    shaderData.addColor().addDepth().addPoints().addClipping();
    const points: HsGraphicPoints = new HsGraphicPoints(
      getUuid(),
      'point cloud',
      geometry,
      new HsPointsMaterial(viewer.gl, shaderData.makeShader())
    );
    points.setPointsSize(viewer.gl, 10);
    points.worldMatrix.copy(makeMatrix(addOrigin([-10, 4, 5]), undefined, [3.5, 3.5, 3.5]));
    points.updateBoundingSphere();
    viewer.add(points);
  }

  /** Adds a tool object that the pointer can drag; tools are not clipped. */
  private addTool(
    viewer: HsHypersceneViewer,
    group: HsGraphicObject[],
    geometry: HsGeometry,
    material: HsMaterial,
    highlight: HsMaterial,
    handle: Omit<ClippingHandle, 'object'>
  ): HsGraphicObject {
    const object: HsGraphicObject = this.addObject(viewer, 'clipping tool', geometry, material);
    group.push(object);
    this.handles.set(object, { object, ...handle });
    this.highlightMaterials.set(object, highlight);
    return object;
  }

  private addPlaneTools(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const planeGeometry: HsGeometry | null = hsMakeGeometry(graphics, hsGeneratePlaneGeometryData());
    const arrowGeometry: HsGeometry | null = makeArrowGeometry(graphics, 5);
    if (planeGeometry === null || arrowGeometry === null) {
      return;
    }
    const face: Omit<ClippingHandle, 'object'> = { kind: HandleKind.Plane, axis: 0, side: 0, arrow: false };
    const highlight: HsMaterial = makeToolMaterial(viewer.gl, HIGHLIGHT_COLOR, 0.3);
    this.addTool(viewer, this.planeTools, planeGeometry, makeToolMaterial(viewer.gl, TOOL_COLOR, 0.3), highlight, face);
    this.addTool(
      viewer,
      this.planeTools,
      arrowGeometry,
      makeLitMaterial(viewer.gl, ...ARROW_COLORS[0]),
      makeLitMaterial(viewer.gl, ...HIGHLIGHT_COLOR),
      { ...face, arrow: true }
    );
  }

  private addBoxTools(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const planeGeometry: HsGeometry | null = hsMakeGeometry(graphics, hsGeneratePlaneGeometryData());
    const arrowGeometry: HsGeometry | null = makeArrowGeometry(graphics, 3);
    if (planeGeometry === null || arrowGeometry === null) {
      return;
    }
    const faceMaterial: HsMaterial = makeToolMaterial(viewer.gl, TOOL_COLOR, 0.3);
    const faceHighlight: HsMaterial = makeToolMaterial(viewer.gl, HIGHLIGHT_COLOR, 0.3);
    const arrowHighlight: HsMaterial = makeLitMaterial(viewer.gl, ...HIGHLIGHT_COLOR);
    for (let axis: number = 0; axis < 3; axis++) {
      const arrowMaterial: HsMaterial = makeLitMaterial(viewer.gl, ...ARROW_COLORS[axis]);
      for (let side: number = 0; side < 2; side++) {
        const face: Omit<ClippingHandle, 'object'> = { kind: HandleKind.Face, axis, side, arrow: false };
        this.addTool(viewer, this.boxTools, planeGeometry, faceMaterial, faceHighlight, face);
        this.addTool(viewer, this.boxTools, arrowGeometry, arrowMaterial, arrowHighlight, { ...face, arrow: true });
      }
      const moveHandle: Omit<ClippingHandle, 'object'> = { kind: HandleKind.Box, axis, side: 0, arrow: true };
      this.addTool(viewer, this.boxTools, arrowGeometry, arrowMaterial, arrowHighlight, moveHandle);
    }
    // The helper box spans 0..1, so placing it at the box minimum and scaling it by the box size outlines the box.
    this.boxLines = new HsBoxHelper(graphics, new HsBox(0, 0, 0, 1, 1, 1), HsColor.makeBlack()).makeLines();
    this.boxTools.push(this.boxLines);
    viewer.add(this.boxLines);
  }

  private startDrag(viewer: HsHypersceneViewer, event: MouseEvent): void {
    if (event.button !== 0) {
      return;
    }
    const pick: HsViewerPick | null = viewer.pick(event.clientX, event.clientY);
    const handle: ClippingHandle | undefined = pick === null ? undefined : this.handles.get(pick.object);
    if (handle === undefined) {
      return;
    }
    this.startAnchor.setFromMatrixPosition(handle.object.worldMatrix.values);
    const parameter: number | null = closestAxisParameter(
      this.startAnchor,
      handle.axis,
      makePointerRay(viewer, event.clientX, event.clientY)
    );
    if (parameter === null) {
      return;
    }
    this.startParameter = parameter;
    this.startPlaneX = this.planeX;
    this.startBoxMin.copy(this.boxMin);
    this.startBoxMax.copy(this.boxMax);
    this.dragged = handle;
  }

  private drag(handle: ClippingHandle, ray: PointerRay): void {
    const parameter: number | null = closestAxisParameter(this.startAnchor, handle.axis, ray);
    if (parameter === null) {
      return;
    }
    const delta: number = parameter - this.startParameter;
    const axis: number = handle.axis;
    const lowest: number = ORIGIN[axis] - BOX_MAX_DISTANCE;
    const highest: number = ORIGIN[axis] + BOX_MAX_DISTANCE;
    if (handle.kind === HandleKind.Plane) {
      this.planeX = Math.min(highest, Math.max(lowest, this.startPlaneX + delta));
    } else if (handle.kind === HandleKind.Box) {
      const shift: number = Math.min(
        highest - this.startBoxMax.values[axis],
        Math.max(lowest - this.startBoxMin.values[axis], delta)
      );
      this.boxMin.values[axis] = this.startBoxMin.values[axis] + shift;
      this.boxMax.values[axis] = this.startBoxMax.values[axis] + shift;
    } else if (handle.side === 0) {
      const limit: number = this.boxMax.values[axis] - BOX_MIN_EXTENT;
      this.boxMin.values[axis] = Math.min(limit, Math.max(lowest, this.startBoxMin.values[axis] + delta));
    } else {
      const limit: number = this.boxMin.values[axis] + BOX_MIN_EXTENT;
      this.boxMax.values[axis] = Math.max(limit, Math.min(highest, this.startBoxMax.values[axis] + delta));
    }
  }

  /** Shows the tools of the current mode in their places and sets the clipping planes to match. */
  private updateTools(viewer: HsHypersceneViewer): void {
    this.planeTools.forEach((object: HsGraphicObject) => (object.visible = this.mode === ClippingMode.Plane));
    this.boxTools.forEach((object: HsGraphicObject) => (object.visible = this.mode === ClippingMode.Cuboid));
    this.placePlaneTools();
    this.placeBoxTools();
    this.placeBoxMoveArrows(viewer);
    this.updateClippingPlanes();
    viewer.invalidate();
  }

  private placePlaneTools(): void {
    const center: HsHypersceneTypes.Array3 = addOrigin(PLANE_CENTER);
    center[0] = this.planeX;
    this.handles.forEach((handle: ClippingHandle) => {
      if (handle.kind !== HandleKind.Plane) {
        return;
      }
      handle.object.worldMatrix.copy(
        handle.arrow
          ? makeMatrix(center, makeDirectionRotation(0, 1))
          : makeFaceMatrix(0, center, [0, PLANE_HALF_SIZE, PLANE_HALF_SIZE])
      );
      handle.object.updateBoundingSphere();
    });
  }

  private placeBoxTools(): void {
    const size: HsVector3 = HsVector3.makeCopy(this.boxMax).subtract(this.boxMin);
    const center: HsVector3 = HsVector3.makeCopy(this.boxMin).add(this.boxMax).multiplyByScalar(0.5);
    const halfSizes: HsHypersceneTypes.Array3 = [size.x / 2, size.y / 2, size.z / 2];
    this.handles.forEach((handle: ClippingHandle) => {
      if (handle.kind !== HandleKind.Face) {
        return;
      }
      const position: HsHypersceneTypes.Array3 = [center.x, center.y, center.z];
      position[handle.axis] = (handle.side === 0 ? this.boxMin : this.boxMax).values[handle.axis];
      handle.object.worldMatrix.copy(
        handle.arrow
          ? makeMatrix(position, makeDirectionRotation(handle.axis, handle.side === 0 ? -1 : 1))
          : makeFaceMatrix(handle.axis, position, halfSizes)
      );
      handle.object.updateBoundingSphere();
    });
    if (this.boxLines !== null) {
      this.boxLines.worldMatrix.copy(makeMatrix(this.boxMin.values, undefined, [size.x, size.y, size.z]));
      this.boxLines.updateBoundingSphere();
    }
  }

  /** Puts the arrows that move the whole box on the box corner nearest the camera, pointing away from the box. */
  private placeBoxMoveArrows(viewer: HsHypersceneViewer): void {
    const camera: HsHypersceneTypes.Array3 = viewer.camera.worldMatrix.getPositionArray();
    const center: HsVector3 = HsVector3.makeCopy(this.boxMin).add(this.boxMax).multiplyByScalar(0.5);
    const signs: HsHypersceneTypes.Array3 = [0, 1, 2].map((axis: number) =>
      camera[axis] > center.values[axis] ? 1 : -1
    ) as HsHypersceneTypes.Array3;
    const corner: HsHypersceneTypes.Array3 = [0, 1, 2].map((axis: number) =>
      signs[axis] > 0 ? this.boxMax.values[axis] : this.boxMin.values[axis]
    ) as HsHypersceneTypes.Array3;
    this.handles.forEach((handle: ClippingHandle) => {
      if (handle.kind === HandleKind.Box) {
        handle.object.worldMatrix.copy(makeMatrix(corner, makeDirectionRotation(handle.axis, signs[handle.axis])));
        handle.object.updateBoundingSphere();
      }
    });
  }

  /**
   * A fragment at world point p is discarded when dot(-p, normal) > offset, so a plane keeps the side where
   * dot(p, normal) >= -offset.
   */
  private updateClippingPlanes(): void {
    const planes: HsClippingPlane[] = this.clippingStorage.clippingPlanes;
    planes.forEach((plane: HsClippingPlane) => plane.setComponents(1, 0, 0, DISABLED_OFFSET));
    if (this.mode === ClippingMode.Plane) {
      planes[0].setComponents(-1, 0, 0, this.planeX);
    } else if (this.mode === ClippingMode.Cuboid) {
      for (let axis: number = 0; axis < 3; axis++) {
        const normal: HsHypersceneTypes.Array3 = [0, 0, 0];
        normal[axis] = 1;
        planes[axis * 2].setComponents(...normal, -this.boxMin.values[axis]);
        planes[axis * 2 + 1].setComponents(-normal[0], -normal[1], -normal[2], this.boxMax.values[axis]);
      }
    }
  }

  /** Picks after the frame was drawn and highlights the tool or cube under the pointer. */
  private updateHover(viewer: HsHypersceneViewer): void {
    if (this.dragged !== null || !this.pointerMoved) {
      return;
    }
    this.pointerMoved = false;
    const pick: HsViewerPick | null = this.pointer === null ? null : viewer.pick(this.pointer[0], this.pointer[1]);
    const object: HsGraphicObject | null =
      pick !== null && this.highlightMaterials.has(pick.object) ? pick.object : null;
    if (object === this.hovered) {
      return;
    }
    if (this.hovered !== null && this.hoveredMaterial !== null) {
      this.hovered.material = this.hoveredMaterial;
    }
    this.hovered = object;
    this.hoveredMaterial = object?.material ?? null;
    const highlight: HsMaterial | undefined = object === null ? undefined : this.highlightMaterials.get(object);
    if (object !== null && highlight !== undefined) {
      object.material = highlight;
    }
    viewer.invalidate();
  }

  private listen<K extends keyof DocumentEventMap & keyof HTMLElementEventMap>(
    target: HTMLElement | Document,
    type: K,
    listener: (event: DocumentEventMap[K]) => void
  ): void {
    target.addEventListener(type, listener as EventListener);
    this.listeners.push(() => target.removeEventListener(type, listener as EventListener));
  }
}
